<template>
	<!-- NOTE: This is a link so that the user can right-click 
	on it and select "Save Link As" to download the audio file -->
	<a
		class="audio-player-container"
		:style="cssVars"
		:href="safeSrc"
		@click.prevent="onPlayerClick"
		@dragstart.prevent
	>
		<CirclePlayIcon
			@click="canPlay ? onClickPlay() : null"
			type="regular"
			v-if="!isLoading && !isPlaying"
		/>
		<CirclePauseIcon
			@click="pauseAudio"
			type="regular"
			v-if="!isLoading && isPlaying"
		/>
		<IosSpinner v-if="isLoading" />
		<StyledRange
			:value="currentTime"
			:max="totalTime"
			:disabled="!canPlay"
			@change="onSliderChange"
			@input="onSliderChange"
			ref="slider"
		/>
		<div class="time" v-if="!loading" v-text="durationStr" />
		<ProgressPie v-if="loading" :size="20" :percent="percent" />
	</a>
</template>

<script>
import { browser } from '@/store'
import { CirclePlayIcon, CirclePauseIcon } from '@/icons'
import StyledRange from './StyledRange'
import moment from 'moment'
import IosSpinner from '@/components/IosSpinner'
import { addEventBusEvent, safeUrl } from '@/helpers/utils'
import { ProgressPie } from '@/components/common'

export const AUDIO_PAUSE_EVENT = 'stop-audio'

export const pauseAllAudio = (exceptId) => {
	EventBus.$emit(AUDIO_PAUSE_EVENT, exceptId)
}

export default {
	name: 'AudioPlayer',
	components: {
		StyledRange,
		CirclePlayIcon,
		CirclePauseIcon,
		IosSpinner,
		ProgressPie,
	},
	props: {
		src: String,
		loading: {
			// will display ProgressPie instead of time string
			type: Boolean,
			default: null,
		},
		duration: {
			type: Number,
			default: 0,
		},
		speed: {
			type: Number,
			default: 1,
		},
		percent: {
			// upload percentage for ProgressPie
			type: Number,
			default: 0,
		},
	},
	data() {
		return {
			isPlaying: false,
			canPlay: false,
			isLoading: false,
			totalTime: this.duration,
			currentTime: 0,
			bufferedAmount: 0,
		}
	},
	computed: {
		safeSrc() {
			return safeUrl(this.src)
		},
		cssVars() {
			// if (!this.canPlay) {
			// 	return { '--seek-before-width': '0%', '--buffered-width': '100%' }
			// }
			const beforeWidth = (this.currentTime / this.totalTime) * 100
			const bufferedAmount = (this.bufferedAmount / this.totalTime) * 100
			return {
				'--seek-before-width': `${beforeWidth}%`,
				'--buffered-width': `${bufferedAmount}%`,
			}
		},
		durationStr() {
			return this.formatDuration(this.currentTime || this.totalTime)
		},
		durationEvent() {
			// ff sucks: https://stackoverflow.com/questions/45668754/html5-audio-duration-is-different-in-chrome-and-firefox
			return browser.name === 'firefox' ? 'durationchange' : 'loadedmetadata'
		},
	},
	watch: {
		src() {
			if (!this.audio.src) return
			this.audio.pause()
			if (!this.safeSrc) return
			this.audio.src = this.safeSrc
		},
		speed(speed) {
			this.audio.playbackRate = speed
		},
		isPlaying(isPlaying) {
			if (isPlaying) {
				requestAnimationFrame(this.whilePlaying)
			} else {
				cancelAnimationFrame(this.raf)
			}
		},
	},
	created() {
		this.audio = new Audio()
		this.audio.addEventListener('play', this.onAudioPlay)
		this.audio.addEventListener('pause', this.onAudioPause)
		this.audio.addEventListener('ended', this.onAudioEnded)
		this.audio.addEventListener('playing', this.onAudioPlaying)
		this.audio.addEventListener('waiting', this.onAudioWaiting)
		this.audio.addEventListener('loadeddata', this.onAudioCanPlay)
		this.audio.addEventListener(this.durationEvent, this.setInitialRangeMax)
		this.audio.addEventListener('error', this.onAudioError)
	},
	mounted() {
		this.removeEvent = addEventBusEvent(AUDIO_PAUSE_EVENT, (exceptId) => {
			if (exceptId && exceptId === this._.uid) return
			this.audio.pause()
		})
	},
	beforeUnmount() {
		this.audio.removeEventListener('play', this.onAudioPlay)
		this.audio.removeEventListener('pause', this.onAudioPause)
		this.audio.removeEventListener('ended', this.onAudioEnded)
		this.audio.removeEventListener('playing', this.onAudioPlaying)
		this.audio.removeEventListener('waiting', this.onAudioWaiting)
		this.audio.removeEventListener('loadeddata', this.onAudioCanPlay)
		this.audio.removeEventListener(this.durationEvent, this.setInitialRangeMax)
		this.audio.removeEventListener('error', this.onAudioError)
		this.audio.pause()
		this.audio = null
		this.removeEvent()
	},
	methods: {
		onPlayerClick() {
			if (!this.canPlay) {
				this.onClickPlay()
				return
			}
		},
		onSliderChange(time) {
			this.audio.currentTime = this.currentTime = time
		},
		setInitialRangeMax(e) {
			this.totalTime = this.audio.duration
			if (this.totalTime === Infinity) {
				// the browser wasn't able to figure out the "real" duration (for whatever reason),
				// so we'll use the duration we got from the API as a fallback
				// See https://developer.mozilla.org/en-US/docs/api/v1/HTMLMediaElement/duration
				this.totalTime = this.duration
			}
			this.syncBufferedAmount()
		},
		syncBufferedAmount() {
			const { buffered } = this.audio
			if (!buffered?.length) return
			this.bufferedAmount = buffered.end(buffered.length - 1)
		},
		formatDuration(time) {
			const duration = moment.duration(time * 1000)
			return moment.utc(duration.as('milliseconds')).format('m:ss')
		},
		onClickPlay() {
			if (this.audio.readyState) {
				this.playAudio()
				return
			}
			if (!this.safeSrc) return
			this.isLoading = true
			this.audio.src = this.safeSrc
		},
		whilePlaying() {
			// NOTE: The audio element provides a handy 'timeupdated' event to be notified
			// when the audio.currentTime changes. This would a more efficient way to handle
			// this syncing of the time. But unfortunately that event fires too
			// infrequently for a proper smooth animation of the range's thumb. Instead we
			// are polling the audio.currentTime to ensure we are updated more frequently
			// and can therefore render the thumb animation more smoothly
			if (!this.audio) return
			this.currentTime = this.audio.currentTime
			this.syncBufferedAmount()
			this.rAF = requestAnimationFrame(this.whilePlaying.bind(this))
		},
		onAudioCanPlay() {
			this.isLoading = false
			this.canPlay = true
			this.playAudio()
		},
		onAudioPlay(e) {
			pauseAllAudio(this._.uid)
			this.isPlaying = true
		},
		onAudioPause(e) {
			this.isPlaying = false
		},
		onAudioPlaying(e) {
			this.isLoading = false
		},
		onAudioWaiting() {
			this.isLoading = true
		},
		onAudioEnded() {
			this.isPlaying = false
			this.audio.currentTime = 0
			this.currentTime = 0
		},
		onAudioError(e) {
			console.error('AUDIO ERROR', e)
		},
		async playAudio() {
			if (!this.audio) return
			try {
				await this.audio.play()
			} catch (e) {
				console.error('CANNOT PLAY AUDIO', e)
				this.$alert.error()
			}
		},
		async pauseAudio() {
			if (!this.audio) return
			try {
				await this.audio.pause()
			} catch (e) {
				console.error('CANNOT PAUSE AUDIO', e)
				this.$alert.error()
			}
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.audio-player-container {
	--played-color: var(--blue-300);
	--buffered-color: var(--neutral-300);
	--unbuffered-color: var(--neutral-200);

	padding: 6px 8px;
	border: 1px solid var(--neutral-40040);
	border-radius: 6px;
	max-width: 308px;
	display: flex;
	align-items: center;
	gap: 8px;
	height: 44px;
	cursor: pointer;
	text-decoration: none;
	// since this is a link, we have to prevent the default
	// draggable behavior so that the user can slide the thumb
	// without the entire link being dragged around the screen
	user-select: none;
	-webkit-user-select: none;
	-webkit-user-drag: none;
	-moz-user-select: none;
	-moz-user-drag: none;

	:deep(.pronto-icon) {
		cursor: pointer;
		font-size: 24px;
		flex-shrink: 0;
		color: var(--blue-300);
	}
	:deep(.spinner-div) {
		position: static;
		width: 24px;
		height: 24px;
		#mainSpinner {
			background: transparent !important;
			width: 26px !important;
			height: 26px !important;
			margin-top: 0 !important;
			padding: 0 !important;
		}
	}
	.time {
		color: var(--neutral-800);
		font-size: 14px;
		white-space: nowrap;
	}
	:deep(.styled-range) {
		--gradient: linear-gradient(
			to right,
			var(--played-color) 0%,
			var(--played-color) var(--seek-before-width),
			var(--buffered-color) var(--seek-before-width),
			var(--buffered-color) var(--buffered-width),
			var(--unbuffered-color) var(--buffered-width),
			var(--unbuffered-color) 100%
		) !important;
		&::before {
			width: var(--seek-before-width) !important;
		}
		&::-webkit-slider-runnable-track {
			background: var(--gradient);
		}
		&::-moz-range-track {
			background: var(--gradient);
		}
		&::-ms-fill-upper {
			background: var(--gradient);
		}
	}
}
</style>
